/**
 * subscriptionPaymentService.ts
 * ==============================
 * Handles all communication with the dsatschool-payment server for subscription
 * plan discovery and checkout initiation.
 *
 * Base URL: NEXT_PUBLIC_PAYMENT_SERVER_URL (e.g. https://pay.dsatschool.com)
 */

import axios, { AxiosError } from "axios"
import { authService } from "./auth"

// ── Axios client pointed at the payment server ───────────────────────────────

export const paymentServerApi = axios.create({
    baseURL: process.env.NEXT_PUBLIC_PAYMENT_SERVER_URL,
    timeout: 15_000,
})

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PeriodPricing {
    BDT: number
    USD?: number
}

/** Keyed by billing months: "1" | "3" | "6" */
export type PlanPeriods = Record<string, PeriodPricing>

export interface SubscriptionFeature {
    text?: string
    name?: string
    included: boolean
    sync_quota_service?: string
}

export interface SubscriptionPlan {
    product_key: string
    name: string
    description?: string
    status: string
    banner_url?: string
    order_index: number
    is_popular: boolean
    is_best_value: boolean
    portal_plan_id: number | null
    tier_slug: string
    features: SubscriptionFeature[]
    periods: PlanPeriods
}

export type PaymentGateway = "bkash" | "sslcommerz"
export type BillingCurrency = "BDT" | "USD"
export type BillingMonths = 1 | 3 | 6

export interface CheckoutRequest {
    product_key: string
    months: BillingMonths
    currency: BillingCurrency
    gateway: PaymentGateway
    coupon_code?: string
}

export interface CheckoutResponse {
    success: boolean
    payment_url: string
    order_id: number
    transaction_id: string
    gateway: PaymentGateway
}

export type PaymentStatus =
    | "initiated"
    | "success"
    | "failed"
    | "cancelled"
    | "validation_failed"

export interface OrderStatus {
    transaction_id: string
    order_id: number
    status: PaymentStatus
    product_key: string
    amount: number
    currency: string
    gateway: string | null
    portal_plan_id: string | null
    billing_months: string | null
    purchase_date: string | null
}

// ── Service ───────────────────────────────────────────────────────────────────

export const subscriptionPaymentService = {
    /**
     * List all active subscription plans (with multi-period BDT/USD pricing).
     * Cached for 5 minutes on the payment server.
     */
    getPlans: async (): Promise<SubscriptionPlan[]> => {
        const res = await paymentServerApi.get<{
            success: boolean
            data: SubscriptionPlan[]
        }>("/api/v1/subscriptions/plans")
        return res.data.data
    },

    /**
     * Get a single subscription plan by product_key.
     */
    getPlan: async (productKey: string): Promise<SubscriptionPlan> => {
        const res = await paymentServerApi.get<{
            success: boolean
            data: SubscriptionPlan
        }>(`/api/v1/subscriptions/plans/${productKey}`)
        return res.data.data
    },

    /**
     * Initiate a subscription payment.
     * The portal Bearer token is automatically attached from the auth service.
     *
     * Returns a `payment_url` — redirect the user there to complete payment.
     */
    initiateCheckout: async (req: CheckoutRequest): Promise<CheckoutResponse> => {
        const token = authService.getToken()
        if (!token) throw new Error("You must be logged in to purchase a subscription.")

        try {
            const res = await paymentServerApi.post<CheckoutResponse>(
                "/api/v1/subscriptions/checkout/initiate",
                req,
                { headers: { Authorization: `Bearer ${token}` } },
            )
            return res.data
        } catch (err) {
            const axiosErr = err as AxiosError<{ error?: { message?: string } }>
            const message =
                axiosErr.response?.data?.error?.message ??
                axiosErr.message ??
                "Payment initiation failed"
            throw new Error(message)
        }
    },

    /**
     * Poll the payment status of an order by transaction_id.
     * Requires an authenticated user — the portal JWT is sent automatically.
     */
    getOrderStatus: async (transactionId: string): Promise<OrderStatus> => {
        const token = authService.getToken()
        if (!token) throw new Error("You must be logged in to check payment status.")

        const res = await paymentServerApi.get<{
            success: boolean
            data: OrderStatus
        }>("/api/v1/subscriptions/checkout/status", {
            params: { transaction_id: transactionId },
            headers: { Authorization: `Bearer ${token}` },
        })
        return res.data.data
    },
}
